import { Component, OnDestroy, OnInit, TemplateRef } from "@angular/core";
import { Page, IUserAccount, IResource, GenericResponse, GenericStatus } from "@shared/models";
import { takeUntil, finalize } from "rxjs/operators";
import { Location } from '@angular/common';

import { HttpService, AppData, ResourceService, NotifyService } from "@shared/services";
import { FormGroup, FormBuilder, Validators, FormControl, FormArray } from "@angular/forms";
import { ApiResources, LinqHelper } from "../../../../../shared/helpers";

import { NgbDate, NgbCalendar, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SlotHolder, Bed, WordHelper, NurseDetails, Mode, ViewModel, BedFilter, FetchBedMode } from "../../helpers/helper";
import { ActivatedRoute, Params } from "@angular/router";

@Component({
    templateUrl: "./shift-assign.html",
    styleUrls: ['./shift-assign.css'],
})
export class NurseShiftAssignPage implements OnInit, OnDestroy {

    page: Page;
    mode = Mode;
    currentMode: Mode;
    id: string;
    date: NgbDate;

    mainForm: FormGroup;

    shifts: Array<IResource>;
    loadingShifts: boolean;

    slotsHolder: Array<SlotHolder>;
    selectedSlotIndex: number;

    originalBeds: Array<Bed>;
    beds: Array<Bed>;

    floorsFilter: Array<string>;
    wardsFilter: Array<string>;
    roomsFilter: Array<string>;
    bedsFilter: Array<string>;

    nurse: NurseDetails;
    loadingNurse: boolean;
    loadingBeds: boolean;
    modalRef: NgbModalRef;

    readOnly: boolean;
    isSingle: boolean;
    loading: boolean;
    submitting: boolean;
    submitted: boolean;
    records: Array<ViewModel>;
    dbRecords: Array<ViewModel>;
    removedNurseShiftIds: Array<number>;
    hideEmpty: FormControl;
    hideAssigned: FormControl;

    constructor(
        private readonly appData: AppData,
        private readonly route: ActivatedRoute,
        private readonly httpService: HttpService,
        private readonly formBuilder: FormBuilder,
        private readonly resourceService: ResourceService,
        private readonly notifyService: NotifyService,
        private readonly calendar: NgbCalendar,
        private readonly modalService: NgbModal,
        private readonly location: Location
    ) {
        this.hideEmpty = new FormControl(false);
        this.hideAssigned = new FormControl(false);
        this.readOnly = this.route.snapshot.data['readOnly'] || false;
        this.loading = true;
        this.page = new Page();
        this.init();
        this.buildForm();
    }

    init = () => {
        this.removedNurseShiftIds = Array<number>();
        this.slotsHolder = new Array<SlotHolder>();
        this.beds = new Array<Bed>();
        this.originalBeds = new Array<Bed>();
        this.records = new Array<ViewModel>();
    }

    ngOnDestroy() {
        this.page.unsubscribeAll();
    }

    ngOnInit() {
        this.appData.userAccount
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe((userAccount: IUserAccount) => {
                if (userAccount) {
                    this.page.userAccount = userAccount;
                    this.route.params
                        .subscribe((params: Params) => {
                            this.id = params["id"];
                            var date = params["date"];
                            if (date) {
                                date = JSON.parse(window.atob(date));
                                this.date = new NgbDate(date.year, date.month, date.day);
                                this.currentMode = Mode.Edit;
                                this.isSingle = true;
                            }

                            this.fetchShifts(() => {
                                this.fetch();
                            });

                            this.loadingNurse = true;
                            this.getNurseDetails();
                        });
                } else {
                    this.page.userAccount = undefined;
                }
            });
    }

    get formSlots() { return this.mainForm.get("slots") as FormArray; }
    formSlotBeds(index: number) { return (this.mainForm.get("slots") as FormArray).at(index).get("beds") as FormArray; }
    formSlotBedStatus(index: number, subIndex: number) { return ((this.mainForm.get("slots") as FormArray).at(index).get("beds") as FormArray).at(subIndex).get("status").value; }
    formSlotBedIsShow(index: number, subIndex: number) { return ((this.mainForm.get("slots") as FormArray).at(index).get("beds") as FormArray).at(subIndex).get("isShow").value; }
    formSlotBedValue(index: number, subIndex: number) { return ((this.mainForm.get("slots") as FormArray).at(index).get("beds") as FormArray).at(subIndex).value; }

    private buildForm() {

        var floorName = new FormControl(null);
        var wardName = new FormControl(null);
        var roomName = new FormControl(null);
        var bedName = new FormControl(null);
        var selectAll = new FormControl(false);
        this.mainForm = this.formBuilder.group({
            floorName, wardName, roomName, bedName, hideEmpty: this.hideEmpty, hideAssigned: this.hideAssigned, selectAll,
            slots: new FormArray([], [Validators.required])
        });

        selectAll.valueChanges.subscribe(val => {
            if(val !== null || val !== undefined) {
                this.formSlotBeds(this.selectedSlotIndex).controls.filter(x => x.get("isShow").value).forEach(x => {
                    let bedId = x.get("bedId").value;
                    let found = this.beds.find(y => y.bedId === bedId);
                    if(!found.nurseShiftMapId || (found && found.nurseAccountId === this.nurse.accountId)) {
                        x.get("status").setValue(val);
                    }
                });
            }
        })

        floorName.valueChanges.subscribe(z => {
            if (!z) {
                this.mainForm.patchValue({
                    wardName: null, roomName: null, bedName: null,
                }, { emitEvent: false });

                this.beds = LinqHelper.cloneDeepArray<Bed>(this.originalBeds);
                this.showFormBedsHelper();
                this.setFiltersHelper();
                return;
            }
            this.wardsFilter = this.originalBeds.filter(x => x.floorName === z).map(x => x.wardName).filter(LinqHelper.uniqueOnly);
            this.roomsFilter = this.originalBeds.filter(x => x.floorName === z).map(x => x.roomName).filter(LinqHelper.uniqueOnly);
            this.bedsFilter = this.originalBeds.filter(x => x.floorName === z).map(x => x.bedName).filter(LinqHelper.uniqueOnly);

            this.beds = LinqHelper.cloneDeepArray<Bed>(this.originalBeds.filter(x => x.floorName === z));
            this.showFormBedsHelper();
        });

        wardName.valueChanges.subscribe(z => {
            if (!z) {
                this.mainForm.patchValue({
                    roomName: null, bedName: null,
                }, { emitEvent: false });


                this.beds = LinqHelper.cloneDeepArray<Bed>(this.originalBeds.filter(x =>
                    x.floorName === (floorName.value || x.floorName)
                ));
                this.showFormBedsHelper();
                this.setFiltersHelper();
                return;
            }
            this.floorsFilter = this.originalBeds.filter(x => x.wardName === z).map(x => x.floorName).filter(LinqHelper.uniqueOnly);
            this.roomsFilter = this.originalBeds.filter(x => x.wardName === z).map(x => x.roomName).filter(LinqHelper.uniqueOnly);
            this.bedsFilter = this.originalBeds.filter(x => x.wardName === z).map(x => x.bedName).filter(LinqHelper.uniqueOnly);

            this.beds = LinqHelper.cloneDeepArray<Bed>(this.beds.filter(x => x.wardName === z));
            this.showFormBedsHelper();
        });

        roomName.valueChanges.subscribe(z => {
            if (!z) {
                this.mainForm.patchValue({
                    bedName: null,
                }, { emitEvent: false });

                this.beds = LinqHelper.cloneDeepArray<Bed>(this.originalBeds.filter(x =>
                    x.floorName === (floorName.value || x.floorName) &&
                    x.wardName === (wardName.value || x.wardName)
                ));
                this.showFormBedsHelper();
                this.setFiltersHelper();
                return;
            }
            this.floorsFilter = this.originalBeds.filter(x => x.roomName === z).map(x => x.floorName).filter(LinqHelper.uniqueOnly);
            this.wardsFilter = this.originalBeds.filter(x => x.roomName === z).map(x => x.wardName).filter(LinqHelper.uniqueOnly);
            this.bedsFilter = this.originalBeds.filter(x => x.roomName === z).map(x => x.bedName).filter(LinqHelper.uniqueOnly);

            this.beds = LinqHelper.cloneDeepArray<Bed>(this.beds.filter(x => x.roomName === z));
            this.showFormBedsHelper();
        });

        bedName.valueChanges.subscribe(z => {
            if (!z) {
                this.beds = LinqHelper.cloneDeepArray<Bed>(this.originalBeds.filter(x =>
                    x.floorName === (floorName.value || x.floorName) &&
                    x.wardName === (wardName.value || x.wardName) &&
                    x.roomName === (roomName.value || x.roomName)
                ));
                this.showFormBedsHelper();
                this.setFiltersHelper();
                return;
            }
            this.floorsFilter = this.originalBeds.filter(x => x.bedName === z).map(x => x.floorName).filter(LinqHelper.uniqueOnly);
            this.wardsFilter = this.originalBeds.filter(x => x.bedName === z).map(x => x.wardName).filter(LinqHelper.uniqueOnly);
            this.roomsFilter = this.originalBeds.filter(x => x.bedName === z).map(x => x.roomName).filter(LinqHelper.uniqueOnly);

            this.beds = LinqHelper.cloneDeepArray<Bed>(this.beds.filter(x => x.bedName === z));
            this.showFormBedsHelper();
        });

        this.hideEmpty.valueChanges.subscribe(flag => {
            flag && this.formSlotBeds(this.selectedSlotIndex).controls.filter(x => x.get("isShow").value).forEach(x => {
                x.get("isShow").setValue(!!this.beds.find(y => y.bedId === x.get("bedId").value).admissionNo);
            });

            if (!flag) {
                this.beds = LinqHelper.cloneDeepArray<Bed>(this.originalBeds.filter(x =>
                    x.floorName === (floorName.value || x.floorName) &&
                    x.wardName === (wardName.value || x.wardName) &&
                    x.roomName === (roomName.value || x.roomName) &&
                    x.bedName === (bedName.value || x.bedName)
                ));

                this.showFormBedsHelper();
            }
        });

        this.hideAssigned.valueChanges.subscribe(flag => {
            flag && this.formSlotBeds(this.selectedSlotIndex).controls.filter(x => x.get("isShow").value).forEach(x => {
                x.get("isShow").setValue(!this.beds.find(y => y.bedId === x.get("bedId").value).nurseShiftMapId);
            });

            if (!flag) {
                this.beds = LinqHelper.cloneDeepArray<Bed>(this.originalBeds.filter(x =>
                    x.floorName === (floorName.value || x.floorName) &&
                    x.wardName === (wardName.value || x.wardName) &&
                    x.roomName === (roomName.value || x.roomName) &&
                    x.bedName === (bedName.value || x.bedName)
                ));

                this.showFormBedsHelper();
            }
        });

    }

    setFiltersHelper = () => {
        this.floorsFilter = this.beds.map(x => x.floorName).filter(LinqHelper.uniqueOnly);
        this.wardsFilter = this.beds.map(x => x.wardName).filter(LinqHelper.uniqueOnly);
        this.roomsFilter = this.beds.map(x => x.roomName).filter(LinqHelper.uniqueOnly);
        this.bedsFilter = this.beds.map(x => x.bedName).filter(LinqHelper.uniqueOnly);
    }

    showFormBedsHelper = () => {
        this.formSlotBeds(this.selectedSlotIndex).controls.forEach(x => {
            x.get("isShow").setValue(!!this.beds.find(y => y.bedId === x.get("bedId").value));
            this.hideEmpty.value && x.get("isShow").setValue(!!this.beds.find(y => y.bedId === x.get("bedId").value).admissionNo);
            this.hideAssigned.value && x.get("isShow").setValue(!this.beds.find(y => y.bedId === x.get("bedId").value).nurseShiftMapId);
        });
    }

    removeShift = (index: number) => {
        var nurseshiftId = this.slotsHolder[index].nurseShiftMapId;
        nurseshiftId && this.removedNurseShiftIds.push(nurseshiftId);
        this.slotsHolder.splice(index, 1);
        this.formSlots.removeAt(index);
    }

    addSlot = (model?: ViewModel, callback: Function = () => { }) => {
        var array = this.mainForm.get("slots") as FormArray;
        if (this.slotsHolder.length && !array.at(this.slotsHolder.length - 1).value.toDate) {
            this.notifyService.info("Please complete current assignment to add another");
            return;
        }

        var today = new Date();
        var fromDate = model ? { year: model.fromDate.getFullYear(), month: model.fromDate.getMonth() + 1, day: model.fromDate.getDate() } as NgbDate : null;
        var minDate = model && !this.slotsHolder.length
            ? model.fromDate.getTime() > new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()
                ? this.calendar.getToday()
                : fromDate
            : !this.slotsHolder.length
                ? this.calendar.getToday()
                : this.calendar.getNext(array.at(this.slotsHolder.length - 1).value.toDate as NgbDate);

        var holder = {
            minDate: minDate,
            selectedSlots: new Array<IResource>()
        } as SlotHolder;

        if (model) {
            holder.fromDate = model.fromDate;
            holder.toDate = model.toDate;
            holder.isSameDate = model.fromDate.getTime() === model.toDate.getTime();
            holder.nurseShiftMapId = model.nurseShiftMapId || null;
        }
        this.slotsHolder.push(holder);


        var toDate = model ? { year: model.toDate.getFullYear(), month: model.toDate.getMonth() + 1, day: model.toDate.getDate() } as NgbDate : null;
        var shiftId = model ? model.shiftId || null : null;

        array.push(new FormGroup({
            fromDate: new FormControl(fromDate, [Validators.required]),
            toDate: new FormControl(toDate, [Validators.required]),
            beds: new FormArray([], [Validators.required]),
            shiftId: new FormControl(shiftId, [Validators.required]),
        }));

        shiftId && this.onShiftChange(this.shifts.find(x => x.id === shiftId) as IResource, this.slotsHolder.length - 1);
        callback(this.slotsHolder.length - 1);
    }

    onShiftChange = (shift: IResource, index: number) => {
        this.fetchSlots(shift.id, index);
        this.slotsHolder[index].shiftName = shift.value;
        this.retouchBeds(index);
    }

    retouchBeds = (index: number) => {
        var toDate = this.mainForm.get("slots").value[index].toDate;
        Mode.Edit === this.currentMode &&
            toDate &&
            this.slotsHolder[index].selectedBeds &&
            this.slotsHolder[index].selectedBeds.length > 0 &&
            this.fetchBeds(index, {
                shiftId: this.mainForm.get("slots").value[index].shiftId,
                fromDate: this.mainForm.get("slots").value[index].fromDate,
                toDate: toDate,
                isRetouch: true
            } as BedFilter, FetchBedMode.OnFormEdit);
    }

    private fetchShifts(callback?: Function) {
        this.loadingShifts = true;
        this.resourceService.fetchShifts()
            .pipe(finalize(() => { this.loadingShifts = false }))
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe((response: Array<IResource>) => {
                this.shifts = response;
                callback();
            });
    }

    private fetchSlots(shiftId: number, index: number) {
        this.httpService.post<Array<IResource>>(ApiResources.getURI(ApiResources.resources.base, ApiResources.resources.fetchSlots), shiftId)
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe((response: Array<IResource>) => {
                this.slotsHolder[index].selectedSlots = response;
            });
    }

    onDateSelection(date: NgbDate, index: number) {
        var array = this.mainForm.get("slots") as FormArray;
        if (!array.at(index).value.fromDate && !array.at(index).value.toDate) {
            array.at(index).get("fromDate").setValue(date);
        } else if (array.at(index).value.fromDate && !array.at(index).value.toDate && date.after(array.at(index).value.fromDate) || date.equals(array.at(index).value.fromDate)) {
            array.at(index).get("toDate").setValue(date);
        } else {
            array.at(index).get("toDate").setValue(null);
            array.at(index).get("fromDate").setValue(date);
        }

        var fromDate = array.at(index).get("fromDate").value as NgbDate;
        if (fromDate) {
            this.slotsHolder[index].fromDate = new Date(fromDate.year, fromDate.month - 1, fromDate.day, 0, 0, 0);
        }

        var toDate = array.at(index).get("toDate").value as NgbDate;
        if (toDate) {
            this.slotsHolder[index].toDate = new Date(toDate.year, toDate.month - 1, toDate.day, 0, 0, 0);
        }

        if (this.slotsHolder.length > index + 1 && toDate) {
            for (var i = index + 1; i < this.slotsHolder.length; i++) {
                var currentToDate = array.at(i - 1).value.toDate as NgbDate
                if (currentToDate) {
                    this.slotsHolder[i].minDate = this.calendar.getNext(currentToDate);
                } else {
                    this.slotsHolder[i].minDate = this.calendar.getNext(toDate);
                }

                if (this.slotsHolder[i].fromDate && (!this.slotsHolder[i - 1].toDate || this.slotsHolder[i - 1].toDate.getTime() >= this.slotsHolder[i].fromDate.getTime())) {
                    this.slotsHolder[i].hoveredDate = null;
                    this.slotsHolder[i].fromDate = null;
                    this.slotsHolder[i].toDate = null;
                    array.at(i).get("toDate").setValue(null);
                    array.at(i).get("fromDate").setValue(null);
                }
            }
        }

        this.retouchBeds(index);
    }

    onMouseEnter = (date: NgbDate, index: number) => {
        this.slotsHolder[index].hoveredDate = date;
    }

    onMouseLeave = (index: number) => {
        this.slotsHolder[index].hoveredDate = null;
    }

    isHovered(date: NgbDate, index: number) {
        var array = this.mainForm.get("slots") as FormArray;
        return array.at(index).value.fromDate && !array.at(index).value.toDate && this.slotsHolder[index].hoveredDate && date.after(array.at(index).value.fromDate) && date.before(this.slotsHolder[index].hoveredDate);
    }

    isInside(date: NgbDate, index: number) {
        var array = this.mainForm.get("slots") as FormArray;
        return array.at(index).value.toDate && date.after(array.at(index).value.fromDate) && date.before(array.at(index).value.toDate);
    }

    isRange(date: NgbDate, index: number) {
        var array = this.mainForm.get("slots") as FormArray;
        return date.equals(array.at(index).value.fromDate) || (array.at(index).value.toDate && date.equals(array.at(index).value.toDate)) || this.isInside(date, index) || this.isHovered(date, index);
    }

    markDisabled = (_date: NgbDate, _current?: any) => {
        return this.isSingle;
    }

    onOpenModel(content: TemplateRef<any>, index: number) {
        if (this.slotsHolder[index].loadingBeds || this.loadingBeds) return;

        var toDate = this.mainForm.get("slots").value[index].toDate;
        var shiftId = this.mainForm.get("slots").value[index].shiftId;
        if (!toDate && !shiftId) {
            this.notifyService.warning("Please select shift and dates.");
            return;
        }

        if (!shiftId) {
            this.notifyService.warning("Please select shift.");
            return;
        }

        if (!toDate) {
            this.notifyService.warning("Please select dates.");
            return;
        }

        this.mainForm.patchValue({selectAll: false}, {emitEvent: false});
        this.selectedSlotIndex = index;
        this.originalBeds = new Array<Bed>();
        this.beds = new Array<Bed>();

        this.fetchBeds(index, {
            nurseShiftMapId: this.slotsHolder[index].nurseShiftMapId,
            fromDate: this.mainForm.get("slots").value[index].fromDate,
            shiftId: shiftId,
            toDate: toDate,
        } as BedFilter, FetchBedMode.OnModalOpen);

        setTimeout(() => {
            this.modalRef = this.modalService.open(content, {
                backdrop: "static",
                keyboard: false,
                centered: true,
                size: "xl",
                windowClass: "custom-modal slots-modal effect-scale"
            });
        });
    }

    onCloseModal() {
        try {
            this.modalRef.close();
            this.modalRef = undefined;
        } catch (e) {
            // ignored;
        }
    }

    onBack = () => {
        this.location.back();
    }

    onBedSelected = () => {
        var selectedBedIds = (this.formSlotBeds(this.selectedSlotIndex).value as Array<any>).filter(x => x.status).map(x => x.bedId);
        this.slotsHolder[this.selectedSlotIndex].selectedBeds = LinqHelper.cloneDeepArray<Bed>(this.originalBeds.filter(x => selectedBedIds.indexOf(x.bedId) !== -1));
        this.onCloseModal()
    }

    removeBed = (slotIndex: number, bedIndex: number) => {
        var beds = this.slotsHolder[slotIndex].selectedBeds;
        this.slotsHolder[slotIndex].selectedBeds = beds.filter(x => x.bedId !== beds[bedIndex].bedId);
        this.formSlotBeds(slotIndex).removeAt(bedIndex);
    }

    resetFilter = () => {
        this.floorsFilter = this.originalBeds.map(x => x.floorName).filter(LinqHelper.uniqueOnly);
        this.wardsFilter = this.originalBeds.map(x => x.wardName).filter(LinqHelper.uniqueOnly);
        this.roomsFilter = this.originalBeds.map(x => x.roomName).filter(LinqHelper.uniqueOnly);
        this.bedsFilter = this.originalBeds.map(x => x.bedName).filter(LinqHelper.uniqueOnly);

        this.mainForm.patchValue({
            floorName: null, wardName: null, roomName: null, bedName: null, hideEmpty: false, hideAssigned: false
        }, { emitEvent: false });

        this.beds = LinqHelper.cloneDeepArray<Bed>(this.originalBeds);
        this.showFormBedsHelper();
    }

    reset = () => {
        this.init();
        this.buildForm();
        setTimeout(() => {
            this.fetch();
        });
    }

    getNurseDetails = () => {
        this.httpService.post<GenericResponse>(ApiResources.getURI(ApiResources.nurseShift.base, ApiResources.nurseShift.getNurse), { id: this.id })
            .pipe(takeUntil(this.page.unSubscribe))
            .pipe(finalize(() => { this.loadingNurse = false }))
            .subscribe((response: GenericResponse) => {
                if (response.status === GenericStatus[GenericStatus.Success]) {
                    this.nurse = response.data as NurseDetails;
                } else {
                    this.notifyService.defaultError();
                }
            });
    }

    fetch = () => {
        this.httpService.post<GenericResponse>(ApiResources.getURI(ApiResources.nurseShift.base, ApiResources.nurseShift.fetch), { id: this.id, date: this.date || null })
            .pipe(takeUntil(this.page.unSubscribe))
            .pipe(finalize(() => { this.loading = false }))
            .subscribe((response: GenericResponse) => {
                if (response.status === GenericStatus[GenericStatus.Success]) {
                    var records = response.data as Array<ViewModel>;

                    if (records.length > 0) {
                        this.currentMode = Mode.Edit;
                        records.forEach(x => {

                            x.fromDate = this.isSingle
                                ? new Date(this.date.year, this.date.month - 1, this.date.day)
                                : new Date(x.fromDate);
                            x.toDate = this.isSingle
                                ? new Date(this.date.year, this.date.month - 1, this.date.day)
                                : new Date(x.toDate);
                            
                            this.addSlot(x, (index: number) => {
                                this.fetchBeds(index, {
                                    shiftId: x.shiftId,
                                    nurseShiftMapId: x.nurseShiftMapId
                                } as BedFilter, FetchBedMode.OnInitial)
                            });
                        });
                    } else {
                        this.currentMode = Mode.New;
                        this.addSlot(this.isSingle 
                            ? {
                                fromDate: new Date(this.date.year, this.date.month - 1, this.date.day),
                                toDate: new Date(this.date.year, this.date.month - 1, this.date.day)
                            } as ViewModel
                            : null);
                    }

                } else {
                    this.notifyService.defaultError();
                }
            });
    }

    fetchBeds = (index: number, filterData: BedFilter, fetchMode: FetchBedMode) => {
        this.loadingBeds = true;
        this.slotsHolder[index].loadingBeds = true;
        filterData.nurseAccountId = this.nurse ? this.nurse.accountId : null;

        this.httpService.post<GenericResponse>(ApiResources.getURI(ApiResources.nurseShift.base, ApiResources.nurseShift.fetchBeds), filterData)
            .pipe(takeUntil(this.page.unSubscribe))
            .pipe(finalize(() => { this.slotsHolder[index].loadingBeds = false; this.loadingBeds = false; }))
            .subscribe((response: GenericResponse) => {
                if (response.status === GenericStatus[GenericStatus.Success]) {
                    var data = response.data as Array<Bed>;
                    if (FetchBedMode.OnInitial === fetchMode || FetchBedMode.OnModalOpen === fetchMode) {
                        var array = this.formSlotBeds(index) as FormArray;
                        array.clear();
                        data.forEach((x: Bed, bedIndex: number) => {
                            x.floorName = LinqHelper.stuffIn(x.floorName, WordHelper.floorName);
                            x.wardName = LinqHelper.stuffIn(x.wardName, WordHelper.wardName);
                            x.roomName = LinqHelper.stuffIn(x.roomName, WordHelper.roomName);
                            x.bedIndex = bedIndex;
                            if (x.admissionNo) {
                                x.patientName = x.patientName.toLowerCase();
                                x.doctorName = x.doctorName.toLowerCase();
                            }
                            if (x.nurseShiftMapId) {
                                x.fromDate = new Date(x.fromDate);
                                x.toDate = new Date(x.toDate);
                            }

                            var status = FetchBedMode.OnInitial === fetchMode
                                ? new FormControl(x.nurseShiftMapId && x.nurseShiftMapId === filterData.nurseShiftMapId)
                                : this.slotsHolder[index].selectedBeds && this.slotsHolder[index].selectedBeds.length > 0
                                    ? new FormControl(!!this.slotsHolder[index].selectedBeds.find(y => y.bedId === x.bedId))
                                    : new FormControl(false);

                            array.push(new FormGroup({
                                bedId: new FormControl(x.bedId),
                                status: status,
                                isShow: new FormControl(true)
                            }));
                        })

                        this.floorsFilter = data.map(x => x.floorName).filter(LinqHelper.uniqueOnly);
                        this.wardsFilter = data.map(x => x.wardName).filter(LinqHelper.uniqueOnly);
                        this.roomsFilter = data.map(x => x.roomName).filter(LinqHelper.uniqueOnly);
                        this.bedsFilter = data.map(x => x.bedName).filter(LinqHelper.uniqueOnly);
                        this.originalBeds = data;
                        this.beds = data;

                        if (FetchBedMode.OnInitial === fetchMode) {
                            this.slotsHolder[index].selectedBeds = LinqHelper.cloneDeepArray<Bed>(this.originalBeds.filter(x => x.nurseShiftMapId && x.nurseShiftMapId === filterData.nurseShiftMapId))
                            this.slotsHolder[index].dbBeds = LinqHelper.cloneDeepArray<Bed>(this.slotsHolder[index].selectedBeds)
                        }
                    } else {
                        data.length > 0 && this.slotsHolder[index].selectedBeds.forEach(x => {
                            var found = data.find(y => y.bedId === x.bedId);

                            if (found && found.nurseShiftMapId && x.nurseShiftMapId && x.nurseShiftMapId !== found.nurseShiftMapId) {
                                x.isUnavailable = true;
                                x.assignedFullName = found.fullName;
                                x.assignedRoleName = found.roleName;
                            } else {
                                x.isUnavailable = false;
                            }
                        });
                    }
                } else {
                    this.notifyService.defaultError();
                }
            });
    }

    onSubmit = () => {
        this.submitted = true;
        if (this.mainForm.invalid || this.submitting) return;

        if (this.slotsHolder.filter(x => x.selectedBeds.filter(y => y.isUnavailable).length).length) {
            this.notifyService.error("Please remove Not Available beds and try again.");
            return;
        }

        this.submitting = true;
        var data = {};
        var url = ApiResources.nurseShift.insert;
        if (Mode.New === this.currentMode) {
            data = {
                id: this.id,
                createdBy: this.page.userAccount.accountId,
                shifts: this.slotsHolder.map((x, i: number) => ({
                    fromDate: this.mainForm.get("slots").value[i].fromDate,
                    toDate: this.mainForm.get("slots").value[i].toDate,
                    shiftId: this.mainForm.get("slots").value[i].shiftId,
                    bedIds: x.selectedBeds.map(y => y.bedId)
                }))
            }
        } else {
            url = ApiResources.nurseShift.update;

            data = {
                id: this.id,
                createdBy: this.page.userAccount.accountId,
                isSingle: this.isSingle,
                shifts: this.slotsHolder.map((x, i: number) => {
                    var selectedItems = x.selectedBeds.map(y => y.bedId);
                    var removedItems = [];
                    var addedItems = [];

                    if (x.nurseShiftMapId) {
                        const dbItems = x.dbBeds.map(y => y.bedId);
                        removedItems = dbItems.filter(x => !selectedItems.includes(x));
                        addedItems = selectedItems.filter(x => !dbItems.includes(x));
                    }

                    var shift = this.mainForm.get("slots").value[i];
                    return {
                        nurseShiftMapId: x.nurseShiftMapId,
                        fromDate: shift.fromDate,
                        toDate: shift.toDate,
                        shiftId: shift.shiftId,
                        removedBedIds: removedItems,
                        bedIds: !x.nurseShiftMapId || this.isSingle ? selectedItems : [],
                        addedBedIds: addedItems
                    }
                })
            }

            this.removedNurseShiftIds.length > 0 &&
                this.removedNurseShiftIds.forEach(x => {
                    (data["shifts"] as Array<any>).push({
                        nurseShiftMapId: x,
                        isRemove: true
                    })
                })
        }

        this.httpService.post<GenericResponse>(ApiResources.getURI(ApiResources.nurseShift.base, url), data)
            .pipe(takeUntil(this.page.unSubscribe))
            .pipe(finalize(() => { this.submitting = false }))
            .subscribe((response: GenericResponse) => {
                if (response.status === GenericStatus[GenericStatus.Success]) {
                    this.notifyService.info("Shift has been saved successfully");
                    this.reset();
                } else {
                    this.notifyService.defaultError();
                }
            });

    }

}